import { AutocompleteGenericEntity } from '@australiangreens/ag-internal-components';
import { Ref, useCallback, useImperativeHandle, useMemo, useState } from 'react';

import { SearchTemplateMetadata } from '@/apis/searchtemplates';
import { DOMAINS_WITH_IDS } from '@/constants';
import { SearchTemplate, SearchTreeNode } from '@/types';
import { useUserDetails } from '@hooks/useUserDetails';
import { TemplatePlaceholderWithNodeAndFilterContext } from '@pages/SearchTemplatePage/types';
import {
  createSearchTemplateJson,
  findPlaceholdersInSearchTree,
  getPlaceholderKeysInPositionOrder,
  placeholderFieldHash,
} from '@pages/SearchTemplatePage/util';
import {
  getPlaceholderAriaTitlesByKey,
  mergePlaceholderKeyOrder,
  regeneratePlaceholdersData,
} from './util';
import { TemplatePanelContentHandle } from './templatePanelContentTypes';

export type UseTemplatePanelContentParams = {
  ref?: Ref<TemplatePanelContentHandle>;
  initialMetadata: SearchTemplateMetadata | undefined;
  searchTemplateId: string | undefined;
  initialSearchTree: SearchTreeNode | undefined;
  searchTreeIsIncomplete: boolean;
  hiddenPlaceholderKeys: Set<string>;
  onAnyChange: () => void;
  onTemplateSaveSuccess?: (targetPath: string) => void;
  onPlaceholderClick?: (placeholderKey: string) => void;
  onPlaceholderBlur?: () => void;
  selectedPlaceholderKey?: string | null;
};

export function useTemplatePanelContent({
  ref,
  initialMetadata,
  searchTemplateId,
  initialSearchTree,
  searchTreeIsIncomplete,
  onAnyChange,
}: UseTemplatePanelContentParams) {
  const { data: userDetails } = useUserDetails();

  const [templateDescription, setTemplateDescription] = useState<string>(
    initialMetadata?.description || ''
  );
  const preFilteredDomains = DOMAINS_WITH_IDS.filter(
    (x) => userDetails?.domainToUserLevels[x.id] === 'Principal Coordinator'
  );

  const initialDomainsToShow = initialMetadata?.organisations
    ? DOMAINS_WITH_IDS.filter((x) => initialMetadata?.organisations?.includes(x.id.toString()))
    : [];
  const [templateOrganisations, setTemplateOrganisations] =
    useState<AutocompleteGenericEntity[]>(initialDomainsToShow);

  const [isSaveTemplateDialogOpen, setIsSaveTemplateDialogOpen] = useState(false);
  const [isUpdateOrSaveAsNewTemplateDialogOpen, setIsUpdateOrSaveAsNewTemplateDialogOpen] =
    useState(false);
  const [isUpdateTemplateDialogOpen, setIsUpdateTemplateDialogOpen] = useState(false);

  const [internalSearchTree, setInternalSearchTree] = useState<SearchTreeNode | undefined>(
    initialSearchTree
  );

  const [searchTemplateForPreview, setSearchTemplateForPreview] = useState<
    SearchTemplate | undefined
  >();

  const [placeholdersData, setPlaceholdersData] = useState<
    Record<string, TemplatePlaceholderWithNodeAndFilterContext>
  >(() => findPlaceholdersInSearchTree(initialSearchTree));

  const handleChangeTemplateDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateDescription(event.target.value);
    if (searchTemplateId) {
      onAnyChange();
    }
  };

  const placeholdersDataIsEmpty = Object.keys(placeholdersData).length === 0;

  const [placeholderKeysInAddedOrder, setPlaceholderKeysInAddedOrder] = useState<string[]>(() =>
    getPlaceholderKeysInPositionOrder(findPlaceholdersInSearchTree(initialSearchTree))
  );

  const ariaTitlesByKey = useMemo(
    () => getPlaceholderAriaTitlesByKey(placeholderKeysInAddedOrder, placeholdersData),
    [placeholderKeysInAddedOrder, placeholdersData]
  );

  const handleChangeOrganisations = (newValue: AutocompleteGenericEntity[]) => {
    setTemplateOrganisations(newValue);
    if (searchTemplateId) {
      onAnyChange();
    }
  };

  const organisationsToSave =
    preFilteredDomains.length === 1
      ? preFilteredDomains.map((x) => x.id as string)
      : templateOrganisations.map((x) => x.id as string);

  const handlePlaceholderChange = (
    updatedPlaceholderData: TemplatePlaceholderWithNodeAndFilterContext
  ) => {
    const key = placeholderFieldHash(
      updatedPlaceholderData.leafNodeId,
      updatedPlaceholderData.filterNameAndField
    );

    setPlaceholdersData((prev) => ({
      ...prev,
      [key]: updatedPlaceholderData,
    }));

    if (searchTemplateId) {
      onAnyChange();
    }
  };

  const handleScrollToLeafNode = useCallback((leafNodeId: string) => {
    const el = document.querySelector(`[data-id="${leafNodeId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const saveDisabled =
    placeholdersDataIsEmpty ||
    Object.values(placeholdersData).some(
      (x) => !x.placeholder.description || x.placeholder.description.length < 3
    ) ||
    templateDescription.length < 3 ||
    organisationsToSave.length === 0 ||
    searchTreeIsIncomplete;

  const previewDisabled = saveDisabled;

  const handlePositionsChange = (newPositions: Record<string, number>) => {
    setPlaceholdersData((prev) => {
      const newPlaceholdersData = { ...prev };
      Object.entries(newPositions).forEach(([key, position]) => {
        newPlaceholdersData[key].placeholder.position = position;
      });
      return newPlaceholdersData;
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      getPlaceholdersDataSnapshot: () => structuredClone(placeholdersData),
      syncSearchTree: (newSearchTree: SearchTreeNode | undefined) => {
        const newPlaceholdersData = regeneratePlaceholdersData(placeholdersData, newSearchTree);
        setPlaceholdersData(newPlaceholdersData);
        setInternalSearchTree(newSearchTree);
        setPlaceholderKeysInAddedOrder((prev) => mergePlaceholderKeyOrder(prev, newSearchTree));
      },
    }),
    [placeholdersData, setPlaceholdersData, setInternalSearchTree, setPlaceholderKeysInAddedOrder]
  );

  const handlePreview = () => {
    const searchTemplateJson = createSearchTemplateJson(internalSearchTree, placeholdersData);

    const ourSearchTemplate = {
      id: Number(searchTemplateId) || 0,
      createdAt: '',
      updatedAt: '',
      name: initialMetadata?.name || 'New Template',
      description: templateDescription,
      createdBySub: '',
      isDeleted: false,
      searchTemplateJson,
      organisations: organisationsToSave,
    } as SearchTemplate;

    setSearchTemplateForPreview(ourSearchTemplate);
  };

  const handleSaveClick = () => {
    if (searchTemplateId) {
      setIsUpdateOrSaveAsNewTemplateDialogOpen(true);
    } else {
      setIsSaveTemplateDialogOpen(true);
    }
  };

  return {
    templateDescription,
    handleChangeTemplateDescription,
    preFilteredDomains,
    templateOrganisations,
    handleChangeOrganisations,
    placeholdersDataIsEmpty,
    placeholdersData,
    handlePositionsChange,
    handlePlaceholderChange,
    ariaTitlesByKey,
    handleScrollToLeafNode,
    previewDisabled,
    saveDisabled,
    handlePreview,
    handleSaveClick,
    isSaveTemplateDialogOpen,
    setIsSaveTemplateDialogOpen,
    isUpdateOrSaveAsNewTemplateDialogOpen,
    setIsUpdateOrSaveAsNewTemplateDialogOpen,
    isUpdateTemplateDialogOpen,
    setIsUpdateTemplateDialogOpen,
    internalSearchTree,
    organisationsToSave,
    searchTemplateForPreview,
    setSearchTemplateForPreview,
  };
}
